import { useState, KeyboardEvent } from "react";

import { Button } from "@/components/ui/button";

const KNOWN_COMMANDS = new Set([
  "analyze",
  "status",
  "history",
  "approve",
  "reject",
  "help",
]);

export interface ChatInputSubmit {
  type: "message" | "command";
  content?: string;
  command?: string;
  args?: string[];
}

interface Props {
  onSubmit: (payload: ChatInputSubmit) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function ChatInput({
  onSubmit,
  disabled,
  placeholder = "Escribe un mensaje o /comando…",
}: Props) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    const text = value.trim();
    if (!text) return;
    if (text.startsWith("/")) {
      const tokens = parseCommandTokens(text.slice(1));
      const cmd = (tokens[0] ?? "").toLowerCase();
      if (!KNOWN_COMMANDS.has(cmd)) {
        setError(`Comando desconocido: /${cmd || ""}. Prueba /help`);
        return;
      }
      onSubmit({ type: "command", command: cmd, args: tokens.slice(1) });
    } else {
      onSubmit({ type: "message", content: text });
    }
    setError(null);
    setValue("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col gap-1 border-t bg-card p-3">
      {error && (
        <div className="rounded border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive">
          {error}
        </div>
      )}
      <div className="flex items-end gap-2">
        <textarea
          className="min-h-[44px] w-full resize-none rounded-md border bg-background p-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          rows={2}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
        />
        <Button onClick={handleSubmit} disabled={disabled || !value.trim()}>
          Enviar
        </Button>
      </div>
    </div>
  );
}

/**
 * Tokeniza una línea estilo shell respetando comillas. Equivalente
 * mínimo de shlex.split. Suficiente para los argumentos de los slash
 * commands: `/reject 12345 "no es buen momento"`.
 */
export function parseCommandTokens(input: string): string[] {
  const tokens: string[] = [];
  let i = 0;
  while (i < input.length) {
    while (i < input.length && /\s/.test(input[i])) i++;
    if (i >= input.length) break;
    if (input[i] === '"' || input[i] === "'") {
      const quote = input[i];
      i++;
      let current = "";
      while (i < input.length && input[i] !== quote) {
        current += input[i];
        i++;
      }
      i++; // skip closing quote
      tokens.push(current);
    } else {
      let current = "";
      while (i < input.length && !/\s/.test(input[i])) {
        current += input[i];
        i++;
      }
      tokens.push(current);
    }
  }
  return tokens;
}
